<template>
  <section class="settings-page">
    <div class="upper-line"></div>

    <!--
    <div class="cell-body a11y-body">
      <span>{{ t('lowVisionMode') }}</span>
      <label class="switch">
        <input
          type="checkbox"
          :checked="isLowVisionEnabled"
          @change="onChangeLowVisionMode"
        />
        <span class="slider"></span>
      </label>
    </div>
    -->

    <div v-if="showNotificationsSetting" class="cell-body noty-body">
      <span>{{ t('notifications') }}</span>
      <label class="switch">
        <input
          type="checkbox"
          :checked="notyEnable"
          :disabled="isNotyBusy || !hasPushToken"
          @change="onChangeNotifications"
        />
        <span class="slider"></span>
      </label>
    </div>
    <div v-if="debugStore.apiErrorsSettingVisible" class="cell-body debug-body">
      <span>{{ t('showApiErrors') }}</span>
      <label class="switch">
        <input
          type="checkbox"
          :checked="debugStore.apiErrorsEnabled"
          @change="onChangeApiErrors"
        />
        <span class="slider"></span>
      </label>
    </div>
    <!--
    <div class="cell-body theme-cell">
      <span>{{ t("common.dark_mode_label") }}</span>
      <label class="switch">
        <input
          type="checkbox"
          :checked="themeStore.isDark"
          @change="themeStore.toggleTheme()"
        />
        <span class="slider"></span>
      </label>
    </div>
      -->
    <button
      v-for="item in settingsItems"
      :key="item.key"
      class="cell-body"
      type="button"
      @click="goTo(item.to)"
    >
      <span>{{ t(item.labelKey) }}</span>
      <span class="right-part">
        <span v-if="item.key === 'change-lang'" class="me-2">{{ currentLangLabel }}</span>
        <i class="fa fa-angle-right"></i>
      </span>
    </button>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useLowVisionMode } from '@/composables/useLowVisionMode';
import {
  useDeletePushTokenMutation,
  usePushTokenStateQuery,
  useRegisterPushTokenMutation,
} from "@/queries";
import { useDeviceStore } from "@/stores/device";
import { useDebugStore } from "@/stores/debug";
import { useLangStore } from "@/stores/lang";
import { useThemeStore } from "@/stores/theme";
import { useTopbarStore } from "@/stores/topbar";
import { useViewerStore } from "@/stores/viewer";

const router = useRouter();
const topbarStore = useTopbarStore();
const langStore = useLangStore();
const themeStore = useThemeStore();
const viewerStore = useViewerStore();
const deviceStore = useDeviceStore();
const debugStore = useDebugStore();
const { t } = useI18n();
const { isLowVisionEnabled, setLowVisionMode } = useLowVisionMode();

const settingsItems = [
  { key: "change-lang", to: "/settings/change-lang", labelKey: "change-lang" },
  {
    key: "change-password",
    to: "/settings/change-password",
    labelKey: "change-password",
  },
  { key: "exit", to: "/settings/exit", labelKey: "exit" },
];

const currentLangLabel = computed(() => {
  if (langStore.translation === "kz") return t("kaz_lang");
  return t("rus_lang");
});

const isCordovaApp = computed(
  () => import.meta.env.VITE_APP_PLATFORM === "cordova" || deviceStore.isCordovaReady
);
const showNotificationsSetting = computed(() => isCordovaApp.value);
const userId = computed(() => {
  const fromViewer = Number(viewerStore.profile?.user_id || 0);
  if (fromViewer > 0) return fromViewer;
  try {
    const person = JSON.parse(localStorage.getItem("person") || "{}");
    return Number(person?.profile?.user_id || 0);
  } catch {
    return 0;
  }
});
const pushToken = computed(() =>
  String(deviceStore.phoneToken || localStorage.getItem("phoneToken") || "")
);
const hasPushToken = computed(() => Boolean(pushToken.value));

const notyEnable = ref(false);

const pushTokenStateQuery = usePushTokenStateQuery(pushToken, userId, {
  enabled: computed(
    () => showNotificationsSetting.value && hasPushToken.value && Boolean(userId.value)
  ),
  retry: false,
  staleTime: 0,
});
const registerPushTokenMutation = useRegisterPushTokenMutation({ retry: false });
const deletePushTokenMutation = useDeletePushTokenMutation({ retry: false });

const isNotyBusy = computed(
  () =>
    pushTokenStateQuery.isFetching.value ||
    registerPushTokenMutation.isPending.value ||
    deletePushTokenMutation.isPending.value
);

watch(
  () => pushTokenStateQuery.data.value,
  (list) => {
    if (!Array.isArray(list) || !list.length) {
      notyEnable.value = false;
      return;
    }
    notyEnable.value = Boolean(list[0]?.token);
  },
  { immediate: true }
);

watch(
  () => pushToken.value,
  (value) => {
    if (showNotificationsSetting.value && value && userId.value) {
      pushTokenStateQuery.refetch();
    }
  }
);

function goTo(path) {
  router.push(path);
}

function onChangeLowVisionMode(event) {
  setLowVisionMode(Boolean(event?.target?.checked));
}

function onChangeApiErrors(event) {
  debugStore.setApiErrorsEnabled(Boolean(event?.target?.checked));
}

async function ensurePhoneToken() {
  if (pushToken.value) return pushToken.value;

  const requestPermission =
    window?.cordova?.plugins?.firebase?.messaging?.requestPermission;
  if (typeof requestPermission === "function") {
    try {
      await requestPermission({ forceShow: false });
    } catch {
      return "";
    }
  }

  const getToken = window?.cordova?.plugins?.firebase?.messaging?.getToken;
  if (typeof getToken !== "function") return "";
  try {
    const token = await getToken();
    if (token) deviceStore.setPhoneToken(token);
    return String(token || "");
  } catch {
    return "";
  }
}

async function onChangeNotifications(event) {
  const enable = Boolean(event?.target?.checked);
  if (isNotyBusy.value) return;

  const token = enable ? await ensurePhoneToken() : pushToken.value;
  if (!token || !userId.value) {
    notyEnable.value = false;
    return;
  }

  try {
    if (!enable) {
      await deletePushTokenMutation.mutateAsync({ token });
      notyEnable.value = false;
    } else {
      await registerPushTokenMutation.mutateAsync({
        token,
        userId: userId.value,
        type: 4,
      });
      notyEnable.value = true;
    }
  } finally {
    pushTokenStateQuery.refetch();
  }
}

onMounted(() => {
  viewerStore.initFromStorage();
  topbarStore.setTitle(t("settings"));
  topbarStore.addButton({
    type: "left",
    icon: "keyboard_backspace",
    action: () => router.push("/feed"),
  });
  ensurePhoneToken().then(() => {
    if (showNotificationsSetting.value && pushToken.value && userId.value) {
      pushTokenStateQuery.refetch();
    }
  });
});
</script>

<style scoped>
.settings-page {
  background: var(--surface-color);
  color: var(--text-color);
  border-radius: 8px;
  padding-inline: 24px;
}

@media (max-width: 768px) {
  .settings-page {
    padding: 12px;
  }
}

.upper-line {
  border-top: 1px solid var(--border-color);
}

.cell-body {
  width: 100%;
  color: var(--text-color);
  height: 44px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  border: none;
  border-bottom: 1px dashed var(--border-color);
  background: var(--surface-color);
  justify-content: space-between;
  text-align: left;
}

.right-part {
  display: inline-flex;
  align-items: center;
}

.theme-cell {
  cursor: default;
}

.noty-body,
.debug-body,
.a11y-body {
  cursor: default;
}

.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #c7c7c7;
  transition: 0.2s;
  border-radius: 24px;
}

.slider::before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  top: 3px;
  background-color: #fff;
  transition: 0.2s;
  border-radius: 50%;
}

.switch input:checked + .slider {
  background-color: #36b453;
}

.switch input:checked + .slider::before {
  transform: translateX(22px);
}
</style>
